import { useState } from "react";
import { Button, Col, Modal } from "react-bootstrap";
import PlaylistAutopilotImportUpload from "./PlaylistAutopilotImportUpload";
import PlaylistAutopilotImportSongMatching from "./PlaylistAutopilotImportSongMatching";
import { axiosInstance } from "../../auth/Auth";
import ConfirmPrompt from "../ConfirmPrompt";
import LoadingModal from "../LoadingModal";
import ErrorResponse from "../ErrorResponse";

export default function PlaylistAutopilotImportModal({ show, onClose }) {
    const [data, setData] = useState(null);
    const [inputData, setInputData] = useState({});
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showLoadingModal, setShowLoadingModal] = useState(false);
    const [errorCode, setErrorCode] = useState(null);
    const [plapLoading, setPlapLoading] = useState(false);

    const missingSongsCount = data?.songs.filter(x => !x.matched).length;

    function handleReset() {
        setData(null);
        setShowLoadingModal(false);
        setShowConfirmModal(false);
        setPlapLoading(false);
        setErrorCode(null);
    }

    async function handleSubmitMatches() {
        setShowConfirmModal(false);
        setPlapLoading(true);
        try {
            const matchingData = data.songs.map(x => ({ original: x.original, item_id: x.matched?.item_id, radio_id: inputData.radioId }));
            await axiosInstance.post(`/matching/correction/`, matchingData);
            setErrorCode(null);
            setShowLoadingModal(true);
            const publishResponse = await axiosInstance.post(`/playlist_autopilot/custom/${inputData.radioId}/publish?time_frame=${inputData.timeFrame}`);
            const jobId = publishResponse.data;
            await axiosInstance.get(`/playlist_autopilot/custom/jobs/${jobId}/wait`);
        } catch (error) {
            setErrorCode(error?.response?.status ?? 500);
        } finally {
            setPlapLoading(false);
        }
    }

    return (
        <Modal show={show} onHide={onClose} onExited={handleReset} centered size="xl" fullscreen="md-down" scrollable={!!data}>
            <Modal.Header closeButton>
                <Modal.Title>Import Custom Playlist</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {data ? (
                    <PlaylistAutopilotImportSongMatching
                        data={data}
                        setData={setData}
                        radioId={inputData.radioId}
                        showConfirmModal={showConfirmModal}
                        setShowConfirmModal={setShowConfirmModal}
                        handleSubmitMatches={handleSubmitMatches}
                    />
                ) : (
                    <PlaylistAutopilotImportUpload setData={setData} setInputData={setInputData} />
                )}
                <ConfirmPrompt
                    show={showConfirmModal}
                    handleConfirm={handleSubmitMatches}
                    handleClose={() => setShowConfirmModal(false)}
                    bodyText={`There are still ${missingSongsCount} unmatched entries. Please verify if these songs cannot be matched to existing ones. Otherwise, new songs will be created.`}
                    confirmText="Continue anyway"
                    cancelText="Fix missing matches"
                />
                <LoadingModal
                    show={showLoadingModal}
                    loading={plapLoading}
                    handleConfirm={onClose}
                    handleClose={handleReset}
                    descriptionText="Custom PLAP is working its magic, this can take up to 1 minute. Closing this Dialog will not cancel processing."
                    confirmText="Go to results"
                    cancelText="Run another playlist"
                    titleText="Running Playlist Autopilot..."
                    readyText="Your custom Playlist Autopilot is done. Please click “Go to results” to see your playlist suggestions."
                />
                {errorCode && (
                    <Col xs={12} className="mt-3">
                        <ErrorResponse errorCode={errorCode} />
                    </Col>
                )}
            </Modal.Body>
            {data && (
                <Modal.Footer>
                    <div className="d-flex justify-content-end mt-3">
                        <Button
                            size="sm"
                            variant="ra-dark-blue"
                            className="px-5 text-uppercase fw-bold"
                            onClick={missingSongsCount ? () => setShowConfirmModal(true) : async () => await handleSubmitMatches()}>
                            Next
                        </Button>
                    </div>
                </Modal.Footer>
            )}
        </Modal>
    );
}
